import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../util/AuthContext";
import toast from "react-hot-toast";
import MyLoader from "../../../util/MyLoader";


export default function Login() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const [loading, setLoading] = useState(false);

  // Load saved credentials from localStorage on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    const savedPassword = localStorage.getItem("rememberedPassword");

    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);

      // Auto-fill password if saved (with decryption)
      if (savedPassword) {
        try {
          // Decrypt the password (reverse of encryption used when saving)
          const decryptedPassword = atob(savedPassword);
          setPassword(decryptedPassword);
        } catch (error) {
          console.error("Failed to decrypt password", error);
        }
      }
    }
    const savedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    if (savedTheme === "dark" || (!savedTheme && prefersDark)) {
      setDarkMode(true);
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);
  // Toggle theme and save preference
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result?.success) {
        // Save credentials if "Remember Me" is checked
        if (rememberMe) {
          // Save email in plain text (safe)
          localStorage.setItem("rememberedEmail", email);

          // Save password with simple encoding (NOT secure for production)
          // Consider using more secure encryption in production
          const encodedPassword = btoa(password); // Base64 encode
          localStorage.setItem("rememberedPassword", encodedPassword);
        } else {
          // Clear saved credentials
          localStorage.removeItem("rememberedEmail");
          localStorage.removeItem("rememberedPassword");
        }

       
       
        
        navigate("/dashboard");
        toast.success("Login successful!");

      } else {
      //  toast.error(result?.error || "Login failed");
      }
    } catch (error) {
      toast.error(error.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // Clear saved credentials
  // const handleClearSaved = () => {
  //   localStorage.removeItem("rememberedEmail");
  //   localStorage.removeItem("rememberedPassword");
  //   setEmail("");
  //   setPassword("");
  //   setRememberMe(false);
  //   toast.success("Saved credentials cleared");
  // };
  if (loading) return <MyLoader />;
  return (
    <div className=" min-h-screen grid-background-containermin-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-4 transition-colors duration-300 bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4 transition-colors duration-300">
      {/* Dark mode toggle button */}
      <button
        onClick={toggleDarkMode}
        className="absolute bottom-10 right-10 p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
      >
        {darkMode ? "☀️" : "🌙"}
      </button>

      {/* Login card */}
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 space-y-6">
        {/* Logo / Title */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            AdWave
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">Welcome Back</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Sign in to access your dashboard and manage apps
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email field */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="moon@adwave.in"
              required
            />
          </div>

          {/* Password field */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Password *
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="••••••••"
              required
            />
          </div>

          {/* Remember me and Forgot password */}
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="ml-2">Keep me logged in</span>
            </label>
            <a
              href="#"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Forgot password?
            </a>
          </div>

          {/* Sign in button */}
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
          >
            Sign in
          </button>
        </form>

        {/* Contact support */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          Unable to access?{" "}
          <a
            href="#"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}
